import {
	supplierPrivateNetworksV2ForBuyer,
	supplierPrivateNetworksV2ForSupplier,
} from '../../thunks/supplier_private_networks_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

// Internal teams are the suppliers private to a buyer company — the rows the
// Internal Teams tab of the private network lists. Everything here is shared by
// the picker (InternalTeamSelect) and by read-only consumers that need to turn a
// project's supplierFacilityIds back into names.

// Must also be declared in supplier_private_networks_reducer's initial state —
// getRecordsForTargetCollection throws on a collection the slice doesn't know.
export const INTERNAL_TEAMS_TC = 'INTERNAL_TEAM_SELECT_AUTOCOMPLETE';

// The two flags that narrow the private network to internal teams. Both are sent
// so the pair is unambiguous — see getSupplierTypeFlags in SupplierTypeUtils.
export const INTERNAL_TEAM_FILTERS = { isInternalTeam: true, isLandlord: false };

// A buyer has a handful of internal teams, so one generous page holds all of them.
export const INTERNAL_TEAMS_PAGINATION = { current: 1, pageSize: 50 };

// Rows are hydrated relationships, so the id is the facility's and the label is the
// alias the buyer gave it, falling back to the facility's real name (matching how
// the Internal Teams tab labels the same rows).
export const getInternalTeamId = (relationship) => nullSafeGet('supplierFacilityId', relationship);

export const getInternalTeamName = (relationship) =>
	nullSafeGet('supplierFacilityAlias', relationship) ||
	nullSafeGet('supplierFacility.name', relationship);

// The private-network list writes its hydrated rows into the slice's `distinctRecords`
// map, keyed by supplierFacilityId, and puts only ids in the target collection. But
// getRecordsForTargetCollection (which OWAsyncSelect calls internally) resolves those
// ids against the slice-level `records` map, which this thunk never populates — so the
// dropdown came up empty and disabled itself even on a successful fetch.
//
// Pointing `records` at distinctRecords lines the two up: the collection's ids ARE
// facility ids, which is exactly how distinctRecords is keyed. It also fixes the label
// of a selected team that isn't on the current page, since OWAsyncSelect falls back to
// the same map.
export const asInternalTeamsSlice = (supplierPrivateNetworks) => ({
	...supplierPrivateNetworks,
	records: nullSafeGetOrElse('distinctRecords', supplierPrivateNetworks, {}),
});

// Every internal team fetched so far, for read-only consumers that just need to turn
// ids into names. Callers filter to the ids they care about, so unrelated rows another
// private-network fetch left in the map are harmless.
export const getInternalTeamRecords = (supplierPrivateNetworks) =>
	Object.values(nullSafeGetOrElse('distinctRecords', supplierPrivateNetworks, {}));

export const internalTeamsThunkForRole = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? supplierPrivateNetworksV2ForSupplier
		: supplierPrivateNetworksV2ForBuyer;
